import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { RefreshCw, CheckCircle2, AlertTriangle, Clock } from "lucide-react";

function relTime(iso) {
  if (!iso) return "never";
  const then = new Date(iso).getTime();
  const diffMin = Math.round((Date.now() - then) / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin} min ago`;
  const h = Math.round(diffMin / 60);
  if (h < 24) return `${h} h ago`;
  return `${Math.round(h / 24)} d ago`;
}

function ProviderHealthWidget() {
  const [pmc, setPmc] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/market/providers");
      const found = (r.data?.providers || []).find((p) => p.name === "pmctire_public");
      setPmc(found || null);
    } catch (e) {
      console.error("providers load failed", e);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000); // gentle poll while page open
    return () => clearInterval(t);
  }, [load]);

  const refresh = async () => {
    setBusy(true);
    try {
      const r = await api.post("/market/refresh/pmctire_public");
      if (r.data?.status === "started") {
        toast.success("PMC refresh started — 1-2 minutes.");
      } else if (r.data?.status === "already_running") {
        toast.info("A PMC refresh is already running.");
      }
      // Poll a couple times to catch the completion
      setTimeout(load, 5000);
      setTimeout(load, 30000);
      setTimeout(load, 90000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally {
      setBusy(false);
    }
  };

  if (!pmc) return null;
  const inProgress = (pmc.reason || "").toLowerCase().includes("in progress");
  const ok = pmc.records_last_run > 0 && pmc.last_success;
  return (
    <div className="tm-card p-5 bg-white" data-testid="admin-provider-health-pmc">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-[#3B424B] font-semibold">Provider Health · PMC Tire</div>
          <div className="mt-1 text-sm text-[#0F1115] font-semibold flex items-center gap-1.5">
            {inProgress ? (
              <><Clock size={14} className="text-[#8a6318]" /> Refresh in progress…</>
            ) : ok ? (
              <><CheckCircle2 size={14} className="text-[#14624A]" /> Live</>
            ) : (
              <><AlertTriangle size={14} className="text-[#8a6318]" /> No data yet</>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={busy || inProgress}
          className="tm-btn-outline h-9 inline-flex items-center gap-1.5 text-xs disabled:opacity-50"
          data-testid="admin-pmc-refresh-btn"
        >
          <RefreshCw size={13} className={busy ? "animate-spin" : ""} />
          {busy ? "Starting…" : "Refresh Now"}
        </button>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
        <div>
          <div className="text-xs text-[#3B424B]">Last successful run</div>
          <div className="mt-0.5 font-mono text-[#0F1115]" data-testid="admin-pmc-last-success">
            {relTime(pmc.last_success)}
          </div>
        </div>
        <div>
          <div className="text-xs text-[#3B424B]">Records last run</div>
          <div className="mt-0.5 font-mono text-[#0F1115]" data-testid="admin-pmc-records-last-run">
            {pmc.records_last_run ?? 0}
          </div>
        </div>
      </div>
      {pmc.last_error && (
        <div className="mt-3 text-xs text-[#8a6318]" data-testid="admin-pmc-last-error">
          Last error: {pmc.last_error}
        </div>
      )}
    </div>
  );
}

export default function AdminPage() {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [ea, setEa] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    if (!user?.is_admin) return;
    api.get("/admin/overview").then(r => setOverview(r.data));
    api.get("/admin/early-access").then(r => setEa(r.data));
    api.get("/admin/organizations").then(r => setOrgs(r.data));
    api.get("/admin/uploads").then(r => setUploads(r.data));
  }, [user]);

  if (!user?.is_admin) {
    return <DashboardLayout><div className="tm-card p-8 bg-white" data-testid="admin-forbidden">Admin access required. Only accounts on the treadmargin.com domain have admin privileges.</div></DashboardLayout>;
  }

  return (
    <DashboardLayout>
      <h1 className="text-2xl sm:text-3xl font-bold" data-testid="admin-heading">TreadMargin Admin</h1>
      <p className="mt-1 text-sm text-[#3B424B]">Operational visibility for TreadMargin staff.</p>

      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {overview && Object.entries({
          "Early Access": overview.early_access_count,
          "Organizations": overview.organizations,
          "Users": overview.users,
          "Uploads": overview.uploads,
          "Failed": overview.failed_uploads,
        }).map(([k, v]) => (
          <div key={k} className="tm-card p-5 bg-white" data-testid={`admin-metric-${k.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="text-xs uppercase tracking-wider text-[#3B424B] font-semibold">{k}</div>
            <div className="text-2xl font-bold font-mono mt-2">{v}</div>
          </div>
        ))}
      </div>

      <div className="mt-6">
        <ProviderHealthWidget />
      </div>

      <div className="mt-6 inline-flex bg-white rounded-lg p-1 border border-[var(--tm-border)]">
        {[["early", "Early Access"], ["orgs", "Organizations"], ["uploads", "Uploads"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 rounded-md text-sm font-semibold ${tab === k ? "bg-[var(--tm-charcoal)] text-white" : "text-[#3B424B]"}`} data-testid={`admin-tab-${k}`}>{l}</button>
        ))}
      </div>

      <div className="mt-4 tm-card bg-white overflow-hidden">
        <div className="overflow-x-auto tm-scroll">
          {tab === "early" && (
            <table className="w-full text-sm">
              <thead className="bg-[var(--tm-off)] text-left text-xs uppercase tracking-wider text-[#3B424B]"><tr><th className="p-3">Name</th><th className="p-3">Dealership</th><th className="p-3">Email</th><th className="p-3">City</th><th className="p-3">Province</th><th className="p-3">Locations</th><th className="p-3">Submitted</th></tr></thead>
              <tbody>{ea.map(r => <tr key={r.id} className="border-t border-[var(--tm-border)]"><td className="p-3">{r.name}</td><td className="p-3">{r.dealership}</td><td className="p-3">{r.email}</td><td className="p-3">{r.city}</td><td className="p-3">{r.province}</td><td className="p-3 font-mono">{r.num_locations}</td><td className="p-3 text-[#3B424B]">{new Date(r.created_at).toLocaleString()}</td></tr>)}</tbody>
            </table>
          )}
          {tab === "orgs" && (
            <table className="w-full text-sm">
              <thead className="bg-[var(--tm-off)] text-left text-xs uppercase tracking-wider text-[#3B424B]"><tr><th className="p-3">Name</th><th className="p-3">Contact</th><th className="p-3">City</th><th className="p-3">Province</th><th className="p-3">Locations</th><th className="p-3">Currency</th></tr></thead>
              <tbody>{orgs.map(o => <tr key={o.id} className="border-t border-[var(--tm-border)]"><td className="p-3">{o.name}</td><td className="p-3">{o.primary_contact}</td><td className="p-3">{o.city}</td><td className="p-3">{o.province}</td><td className="p-3 font-mono">{o.num_locations}</td><td className="p-3">{o.currency}</td></tr>)}</tbody>
            </table>
          )}
          {tab === "uploads" && (
            <table className="w-full text-sm">
              <thead className="bg-[var(--tm-off)] text-left text-xs uppercase tracking-wider text-[#3B424B]"><tr><th className="p-3">File</th><th className="p-3">Kind</th><th className="p-3">Rows</th><th className="p-3">Mapped</th><th className="p-3">Created</th></tr></thead>
              <tbody>{uploads.map(u => <tr key={u.id} className="border-t border-[var(--tm-border)]"><td className="p-3 font-mono">{u.filename}</td><td className="p-3">{u.kind}</td><td className="p-3 font-mono">{u.row_count}</td><td className="p-3">{u.mapped ? "yes" : "no"}</td><td className="p-3 text-[#3B424B]">{new Date(u.created_at).toLocaleString()}</td></tr>)}</tbody>
            </table>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
